import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Pencil, Trash2, Package, ImagePlus, X, Loader2, Lock, Star, FlaskConical } from 'lucide-react';

import {
  listCategories,
  listProducts,
  createProduct,
  updateProduct,
  deleteProduct,
  uploadImage,
  productLockedFields,
  vatLabel,
  vatOptionsFor,
  isErp,
  type SupplierProduct,
  type ProductInput,
  type ProductUnit,
  type UnitInput,
} from '@/services/catalog';
import { PageHeader, Spinner, EmptyState, Field, Select, Textarea } from '@/components/fields';
import { useAuthStore } from '@/store/auth.store';

const kopToRub = (k: number) =>
  (k / 100).toLocaleString('ru-RU', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
const rubToKop = (s: string): number => Math.round(parseFloat(s.replace(',', '.')) * 100) || 0;

const activeUnits = (p: SupplierProduct): ProductUnit[] => p.units.filter((u) => u.isActive);

export default function Products() {
  const qc = useQueryClient();
  const devMode = useAuthStore((s) => s.supplier?.erpDeveloperMode) ?? false;
  const [filter, setFilter] = useState<string>('');
  const [editing, setEditing] = useState<SupplierProduct | null>(null);
  const [creating, setCreating] = useState(false);

  const cats = useQuery({ queryKey: ['categories'], queryFn: listCategories });
  const prods = useQuery({ queryKey: ['products'], queryFn: () => listProducts() });

  const catName = useMemo(() => {
    const m = new Map<string, string>();
    cats.data?.forEach((c) => m.set(c.id, c.name));
    return m;
  }, [cats.data]);

  const visible = useMemo(() => {
    const all = prods.data ?? [];
    return filter ? all.filter((p) => p.categoryId === filter) : all;
  }, [prods.data, filter]);

  const remove = useMutation({
    mutationFn: (id: string) => deleteProduct(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['products'] });
      toast.success('Товар удалён');
    },
  });

  return (
    <div>
      <PageHeader
        title="Товары"
        subtitle="Каталог, который видят заведения"
        actions={
          <button className="btn-primary" onClick={() => setCreating(true)}>
            <Plus size={16} /> Товар
          </button>
        }
      />

      {(cats.data?.length ?? 0) > 0 && (
        <div className="flex gap-2 overflow-x-auto no-scrollbar mb-4 -mx-1 px-1">
          <Chip active={!filter} onClick={() => setFilter('')}>
            Все
          </Chip>
          {cats.data!.map((c) => (
            <Chip key={c.id} active={filter === c.id} onClick={() => setFilter(c.id)}>
              {c.name}
            </Chip>
          ))}
        </div>
      )}

      {prods.isLoading ? (
        <Spinner label="Загружаем товары…" />
      ) : visible.length === 0 ? (
        <EmptyState
          icon={<Package size={40} strokeWidth={1.4} />}
          title="Пока нет товаров"
          description={
            (cats.data?.length ?? 0) === 0
              ? 'Сначала добавьте категорию, затем товары.'
              : 'Добавьте первый товар в каталог.'
          }
          action={
            <button className="btn-primary" onClick={() => setCreating(true)}>
              <Plus size={16} /> Добавить товар
            </button>
          }
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((p) => {
            const units = activeUnits(p);
            const extra = units.filter((u) => !u.isDefault);
            return (
              <article key={p.id} className="card p-3 flex gap-3">
                <div className="w-16 h-16 rounded-lg bg-paper border border-line overflow-hidden shrink-0 flex items-center justify-center">
                  {p.imageUrl ? (
                    <img src={p.imageUrl} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <Package size={22} className="text-ink/25" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-ink truncate">{p.name}</div>
                      <div className="text-xs text-ink/45 truncate">
                        {p.categoryId ? catName.get(p.categoryId) ?? '—' : 'Без категории'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1 shrink-0">
                      {isErp(p) && (
                        <span className="badge bg-blue-50 text-blue-700" title="Позицию ведёт 1С">
                          1С
                        </span>
                      )}
                      {!p.isActive && <span className="badge bg-ink/10 text-ink/50">Скрыт</span>}
                    </div>
                  </div>

                  <div className="mt-1.5 text-sm">
                    <span className="font-semibold text-ink">{kopToRub(p.price)} ₽</span>
                    <span className="text-ink/40"> / {p.unit}</span>
                    {p.vatRate > 0 && (
                      <span className="text-ink/35 text-xs"> · НДС {vatLabel(p.vatRate)}</span>
                    )}
                  </div>

                  {extra.length > 0 && (
                    <div className="mt-1 text-xs text-ink/45 truncate">
                      {extra.map((u) => `${u.name} — ${kopToRub(u.price)} ₽`).join(' · ')}
                    </div>
                  )}

                  <div className="mt-2 flex gap-1">
                    <button onClick={() => setEditing(p)} className="btn-outline !px-2 !py-1 text-xs">
                      <Pencil size={13} /> {isErp(p) && !devMode ? 'Открыть' : 'Изменить'}
                    </button>
                    <button
                      onClick={() => {
                        // Позицию из 1С удаляем только в режиме разработчика:
                        // без него обмен вернёт её обратно и человек решит,
                        // что кабинет «не удаляет».
                        if (isErp(p) && !devMode) {
                          toast.error('Позицию ведёт 1С — удалите её в учётной системе');
                          return;
                        }
                        const warn = isErp(p)
                          ? `Удалить «${p.name}»? Позицию ведёт 1С — ближайшая выгрузка создаст её заново.`
                          : `Удалить «${p.name}»?`;
                        if (confirm(warn)) remove.mutate(p.id);
                      }}
                      className="p-1.5 rounded-lg text-ink/40 hover:bg-red-50 hover:text-red-600"
                      title={isErp(p) && !devMode ? 'Ведётся в 1С' : 'Удалить'}
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {(creating || editing) && (
        <ProductModal
          product={editing}
          devMode={devMode}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            qc.invalidateQueries({ queryKey: ['products'] });
            setCreating(false);
            setEditing(null);
          }}
        />
      )}
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`shrink-0 px-3 py-1.5 rounded-full text-sm border transition ${
        active ? 'bg-ink text-white border-ink' : 'bg-white text-ink/70 border-line hover:bg-paper'
      }`}
    >
      {children}
    </button>
  );
}

// ─── Форма товара ───────────────────────────────────────────────────────────

/** Строка редактора единиц: цены держим строкой, чтобы поле не прыгало. */
type UnitRow = {
  key: string;
  unitId: string | null;
  name: string;
  price: string;
  packSize: string;
  isDefault: boolean;
};

const toRows = (product: SupplierProduct | null): UnitRow[] => {
  const units = product ? activeUnits(product) : [];
  if (!units.length) {
    return [{ key: 'u0', unitId: null, name: 'шт', price: '', packSize: '', isDefault: true }];
  }
  return units.map((u, i) => ({
    key: u.id,
    unitId: u.externalId,
    name: u.name,
    price: String(u.price / 100),
    packSize: u.packSize ?? '',
    isDefault: u.isDefault || (i === 0 && !units.some((x) => x.isDefault)),
  }));
};

/** Ставка одна на позицию: у основной единицы, иначе прежняя из карточки. */
const toVatRate = (product: SupplierProduct | null): number => {
  if (!product) return 0;
  const units = activeUnits(product);
  return (units.find((u) => u.isDefault) ?? units[0])?.vatRate ?? product.vatRate;
};

function ProductModal({
  product,
  devMode,
  onClose,
  onSaved,
}: {
  product: SupplierProduct | null;
  devMode: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const cats = useQuery({ queryKey: ['categories'], queryFn: listCategories });

  // Режим разработчика снимает блокировку ERP-полей на запись — но не меняет
  // source/erpFields карточки: ближайший обмен с 1С молча вернёт своё, если
  // она продолжит присылать то же поле (см. erp-lock.ts на бэкенде).
  const erpLocked = product ? productLockedFields(product) : [];
  const lockedFields = devMode ? [] : erpLocked;
  const isLocked = (field: string) => lockedFields.includes(field);
  const unitsLocked = isLocked('units') || isLocked('price') || isLocked('unit');

  const [name, setName] = useState(product?.name ?? '');
  const [categoryId, setCategoryId] = useState(product?.categoryId ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [vatRate, setVatRate] = useState(() => toVatRate(product));
  const [stockQty, setStockQty] = useState(product?.stockQty != null ? String(product.stockQty) : '');
  const [isActive, setIsActive] = useState(product?.isActive ?? true);
  const [imageUrl, setImageUrl] = useState(product?.imageUrl ?? '');
  const [rows, setRows] = useState<UnitRow[]>(() => toRows(product));
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const patchRow = (key: string, patch: Partial<UnitRow>) =>
    setRows((prev) => prev.map((r) => (r.key === key ? { ...r, ...patch } : r)));

  const addRow = () =>
    setRows((prev) => [
      ...prev,
      {
        key: `new-${Date.now()}`,
        unitId: null,
        name: '',
        price: '',
        packSize: '',
        isDefault: prev.length === 0,
      },
    ]);

  const removeRow = (key: string) =>
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      if (next.length && !next.some((r) => r.isDefault)) next[0].isDefault = true;
      return next;
    });

  const onPickImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      setUploading(true);
      const { url } = await uploadImage(file, 'product');
      setImageUrl(url);
    } catch {
      /* тост от интерсептора */
    } finally {
      setUploading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLocked('name') && name.trim().length < 2) {
      toast.error('Укажите название');
      return;
    }

    let units: UnitInput[] | undefined;
    if (!unitsLocked) {
      const filled = rows.filter((r) => r.name.trim() || r.price.trim());
      if (!filled.length) {
        toast.error('Добавьте хотя бы одну единицу с ценой');
        return;
      }
      for (const r of filled) {
        if (!r.name.trim()) {
          toast.error('У единицы не указано название');
          return;
        }
        if (rubToKop(r.price) <= 0) {
          toast.error(`Укажите цену для «${r.name.trim()}»`);
          return;
        }
      }
      const names = filled.map((r) => r.name.trim().toLowerCase());
      if (new Set(names).size !== names.length) {
        toast.error('Единицы измерения повторяются');
        return;
      }
      units = filled.map((r, i) => ({
        unitId: r.unitId,
        name: r.name.trim(),
        price: rubToKop(r.price),
        vatRate,
        packSize: r.packSize.trim() || null,
        isDefault: r.isDefault || (i === 0 && !filled.some((x) => x.isDefault)),
        isActive: true,
        sortOrder: i,
      }));
    }

    // Поля, которыми управляет 1С, не отправляем: сервер их всё равно отклонит.
    const body: ProductInput = {
      name: name.trim(),
      categoryId: categoryId || null,
      description: description.trim() || null,
      imageUrl: imageUrl || null,
      isActive,
    };
    if (isLocked('name')) delete (body as Partial<ProductInput>).name;
    if (isLocked('categoryId')) delete (body as Partial<ProductInput>).categoryId;
    if (isLocked('description')) delete (body as Partial<ProductInput>).description;
    if (isLocked('imageUrl')) delete (body as Partial<ProductInput>).imageUrl;
    if (isLocked('isActive')) delete (body as Partial<ProductInput>).isActive;
    if (!isLocked('stockQty')) {
      body.stockQty = stockQty.trim() === '' ? null : Math.max(0, parseInt(stockQty, 10) || 0);
    }
    if (units) {
      body.units = units;
      // Список полный: единицу, убранную из формы, сервер погасит.
      body.unitsMode = 'replace';
    }

    try {
      setSaving(true);
      if (product) await updateProduct(product.id, body);
      else await createProduct(body);
      toast.success(product ? 'Сохранено' : 'Товар добавлен');
      onSaved();
    } catch {
      /* тост от интерсептора */
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <button className="absolute inset-0 bg-ink/50" onClick={onClose} aria-label="Закрыть" />
      <div className="relative w-full sm:max-w-lg bg-white rounded-t-2xl sm:rounded-2xl shadow-xl max-h-[92vh] overflow-auto">
        <div className="sticky top-0 bg-white border-b border-line px-5 py-3.5 flex items-center justify-between">
          <h3 className="font-semibold text-ink">
            {product ? 'Редактировать товар' : 'Новый товар'}
          </h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-paper text-ink/50">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-5 space-y-4">
          {lockedFields.length > 0 && (
            <div className="flex gap-2 rounded-xl border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
              <Lock size={16} className="mt-0.5 shrink-0" />
              <div>
                Позицию ведёт 1С. Поля с замком меняются только в учётной системе — иначе
                следующий обмен вернёт прежние значения.
              </div>
            </div>
          )}

          {devMode && erpLocked.length > 0 && (
            <div className="flex gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <FlaskConical size={16} className="mt-0.5 shrink-0" />
              <div>
                Режим разработчика: поля 1С временно разблокированы. Правку затрёт ближайший
                обмен, если 1С продолжит присылать то же поле.
              </div>
            </div>
          )}

          {/* Фото */}
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-xl bg-paper border border-line overflow-hidden flex items-center justify-center shrink-0">
              {uploading ? (
                <Loader2 className="animate-spin text-ink/40" size={20} />
              ) : imageUrl ? (
                <img src={imageUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <Package size={24} className="text-ink/25" />
              )}
            </div>
            <div className="flex flex-col gap-2">
              <label className="btn-outline cursor-pointer !py-1.5 text-sm">
                <ImagePlus size={15} /> {imageUrl ? 'Заменить фото' : 'Загрузить фото'}
                <input type="file" accept="image/*" className="hidden" onChange={onPickImage} />
              </label>
              {imageUrl && (
                <button
                  type="button"
                  onClick={() => setImageUrl('')}
                  className="text-xs text-ink/40 hover:text-red-600 text-left"
                >
                  Удалить фото
                </button>
              )}
            </div>
          </div>

          <Field label={isLocked('name') ? 'Название (из 1С)' : 'Название'} required>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={isLocked('name')}
              autoFocus={!product}
            />
          </Field>

          <Field
            label={isLocked('categoryId') ? 'Категория (из 1С)' : 'Категория'}
            hint={isLocked('categoryId') ? 'Папку номенклатуры задаёт учётная система' : undefined}
          >
            <Select
              value={categoryId}
              onChange={(e) => setCategoryId(e.target.value)}
              disabled={isLocked('categoryId')}
            >
              <option value="">Без категории</option>
              {cats.data?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Описание">
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={isLocked('description')}
            />
          </Field>

          <UnitsEditor
            rows={rows}
            locked={unitsLocked}
            onPatch={patchRow}
            onAdd={addRow}
            onRemove={removeRow}
            onDefault={(key) =>
              setRows((prev) => prev.map((r) => ({ ...r, isDefault: r.key === key })))
            }
          />

          <div className="grid grid-cols-2 gap-3">
            <Field label="Ставка НДС" hint="одна на позицию">
              <Select
                value={vatRate}
                onChange={(e) => setVatRate(Number(e.target.value))}
                disabled={unitsLocked || isLocked('vatRate')}
              >
                {vatOptionsFor(vatRate).map((v) => (
                  <option key={v} value={v}>
                    {vatLabel(v)}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Остаток на складе" hint="пусто = не учитывать">
              <input
                className="input"
                inputMode="numeric"
                placeholder="—"
                value={stockQty}
                onChange={(e) => setStockQty(e.target.value.replace(/\D/g, ''))}
                disabled={isLocked('stockQty')}
              />
            </Field>
          </div>

          <label className="flex items-center gap-2.5 text-sm text-ink/80 cursor-pointer">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={isLocked('isActive')}
              className="h-4 w-4 rounded border-line text-orange focus:ring-orange/30"
            />
            Показывать товар в каталоге
          </label>

          <div className="flex gap-2 pt-2">
            <button type="button" className="btn-outline flex-1" onClick={onClose}>
              Отмена
            </button>
            <button type="submit" className="btn-primary flex-1" disabled={saving || uploading}>
              {saving ? 'Сохраняем…' : product ? 'Сохранить' : 'Добавить'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/** Единицы и цены: у одной позиции их может быть несколько («л», «упак», «ящик»). */
function UnitsEditor({
  rows,
  locked,
  onPatch,
  onAdd,
  onRemove,
  onDefault,
}: {
  rows: UnitRow[];
  locked: boolean;
  onPatch: (key: string, patch: Partial<UnitRow>) => void;
  onAdd: () => void;
  onRemove: (key: string) => void;
  onDefault: (key: string) => void;
}) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1.5">
        <span className="label !mb-0 flex items-center gap-1.5">
          Единицы, цены и упаковка
          {locked && <Lock size={12} className="text-ink/40" />}
        </span>
        {!locked && (
          <button type="button" onClick={onAdd} className="text-xs text-orange hover:underline">
            + единица
          </button>
        )}
      </div>

      <div className="rounded-xl border border-line divide-y divide-line overflow-hidden">
        {rows.map((r) => (
          <div key={r.key} className="flex items-center gap-2 p-2">
            <button
              type="button"
              onClick={() => !locked && onDefault(r.key)}
              title={r.isDefault ? 'Основная единица' : 'Сделать основной'}
              disabled={locked}
              className={`p-1.5 rounded-lg shrink-0 ${
                r.isDefault ? 'text-orange' : 'text-ink/25 hover:text-ink/50'
              }`}
            >
              <Star size={15} fill={r.isDefault ? 'currentColor' : 'none'} />
            </button>

            <input
              className="input !py-1.5 w-24 shrink-0"
              placeholder="ед."
              value={r.name}
              onChange={(e) => onPatch(r.key, { name: e.target.value })}
              disabled={locked}
            />
            <div className="relative flex-1 min-w-0">
              <input
                className="input !py-1.5 pr-6"
                inputMode="decimal"
                placeholder="0"
                value={r.price}
                onChange={(e) => onPatch(r.key, { price: e.target.value })}
                disabled={locked}
              />
              <span className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 text-xs text-ink/40">
                ₽
              </span>
            </div>
            <input
              className="input !py-1.5 !w-28 shrink-0"
              placeholder="упаковка"
              title="Упаковка — подпись для заведения, напр. «100 шт/уп» или «ящик 10 кг». На расчёты не влияет"
              value={r.packSize}
              onChange={(e) => onPatch(r.key, { packSize: e.target.value })}
              disabled={locked}
            />

            {!locked && rows.length > 1 && (
              <button
                type="button"
                onClick={() => onRemove(r.key)}
                className="p-1.5 rounded-lg text-ink/35 hover:bg-red-50 hover:text-red-600 shrink-0"
                title="Убрать единицу"
              >
                <X size={15} />
              </button>
            )}
          </div>
        ))}
      </div>

      <span className="mt-1 block text-xs text-ink/40">
        {locked
          ? 'Цены и единицы приходят из 1С'
          : 'Звёздочка — основная единица: её цену видят заведения в списке'}
      </span>
    </div>
  );
}
